import React from 'react'
import './WaveBackground.css'

/**
 * Adds a stylized wavey background to the page.
 *
 * Each of these SVG elements come from the figma design.
 * @see https://www.figma.com/design/9nlSjk3fxhc9mChvqciPEQ/Web-Reader---2024-Concept?node-id=365-1707&p=f
 *
 * @returns {function} Component
 */
export default function WavyBackdrop() {
  return (
    <div id="wavyBackdropContainer">
      {/* ====== Layer 1 – small left sweep ====== */}
      <svg className="wave-left" width="378" height="285" viewBox="0 0 378 285" fill="none">
        <defs>
          <linearGradient
            id={`wave-g1`}
            x1="95.9889"
            y1="71.0784"
            x2="336.867"
            y2="266.901"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#E6E6E6" />
          </linearGradient>
        </defs>
        <path
          d="M261.26 196.517C298.625 155.383 317.448 102.116 342.944 53.6663C352.899 34.7444 364.578 16.8352 377.657 0H-11V284.16C87.4701 276.335 188.506 276.599 261.26 196.51V196.517Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g1)`}
        />
      </svg>

      {/* ====== Layer 2 – mid‑size right sweep ====== */}
      <svg className="wave-left" width="664" height="394" viewBox="0 0 664 394" fill="none">
        <defs>
          <linearGradient
            id={`wave-g2`}
            x1="284.907"
            y1="122.658"
            x2="446.066"
            y2="410.329"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#E6E6E6" />
          </linearGradient>
        </defs>
        <path
          d="M369.304 316.842C399.918 302.91 426.935 282.233 452.713 260.629C538.969 188.348 615.995 101.091 663.708 0H377.657C364.578 16.8352 352.899 34.7383 342.944 53.6663C317.448 102.116 298.625 155.383 261.26 196.517C188.506 276.605 87.4701 276.335 -11 284.16V393.279C77.9569 365.396 172.18 358.191 264.494 344.025C300.288 338.532 336.346 331.842 369.304 316.842Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g2)`}
        />
      </svg>

      {/* ====== Layer 3 – giant left‑to‑right sweep ====== */}
      <svg className="wave-left" width="1042" height="786" viewBox="0 0 1042 786" fill="none">
        <defs>
          <linearGradient
            id={`wave-g3`}
            x1="342.047"
            y1="243.972"
            x2="723.297"
            y2="571.493"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#E6E6E6" />
          </linearGradient>
        </defs>
        <path
          d="M181.282 731.081C271.755 668.14 321.106 562.931 369.316 463.823C417.526 364.715 473.899 261.249 570.804 208.742C629.344 177.024 696.832 167.376 761.245 150.534C864.723 123.48 965.525 73.8218 1041.5 0H663.708C615.995 101.091 538.975 188.348 452.719 260.629C426.941 282.233 399.924 302.916 369.31 316.842C336.352 331.842 300.294 338.538 264.5 344.025C172.186 358.184 77.963 365.396 -10.9939 393.279V785.526C56.7089 788.957 125.59 769.833 181.288 731.081H181.282Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g3)`}
        />
      </svg>

      {/* ====== Layer 4 – giant right‑to‑left sweep ====== */}
      <svg className="wave-right" width="1042" height="786" viewBox="0 0 1042 786" fill="none">
        <defs>
          <linearGradient
            id={`wave-g4`}
            x1="700.327"
            y1="542.034"
            x2="319.078"
            y2="214.507"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#D6D6D6" />
          </linearGradient>
        </defs>
        <path
          d="M861.093 54.9194C770.62 117.859 721.269 223.068 673.059 322.177C624.849 421.285 568.476 524.751 471.571 577.257C413.031 608.976 345.543 618.624 281.13 635.465C177.652 662.519 76.8496 712.178 0.879639 786H378.667C426.38 684.909 503.4 597.652 589.656 525.371C615.434 503.767 642.451 483.084 673.065 469.158C706.023 454.158 742.081 447.462 777.875 441.975C870.189 427.815 964.412 420.604 1053.37 392.721V0.473644C985.666 -2.95722 916.785 16.1672 861.087 54.9194H861.093Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g4)`}
        />
      </svg>

      {/* ====== Layer 5 – mid‑size left sweep ====== */}
      <svg className="wave-right" width="664" height="394" viewBox="0 0 664 394" fill="none">
        <defs>
          <linearGradient
            id={`wave-g5`}
            x1="322.064"
            y1="401.739"
            x2="406.976"
            y2="-395.411"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#D6D6D6" />
          </linearGradient>
        </defs>
        <path
          d="M295.071 77.1577C264.457 91.0898 237.44 111.767 211.662 133.371C125.406 205.652 48.3801 292.909 0.67334 394H286.724C299.803 377.165 311.482 359.262 321.438 340.334C346.933 291.884 365.756 238.617 403.121 197.483C475.869 117.395 576.911 117.665 675.381 109.84V0.721252C586.424 28.6039 492.201 35.8093 399.887 49.9747C364.093 55.4678 328.035 62.1576 295.077 77.1577H295.071Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g5)`}
        />
      </svg>

      {/* ====== Layer 6 – small right sweep ====== */}
      <svg className="wave-right" width="378" height="285" viewBox="0 0 378 285" fill="none">
        <defs>
          <linearGradient
            id={`wave-g6`}
            x1="282.386"
            y1="213.922"
            x2="41.5076"
            y2="18.0986"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#D6D6D6" />
          </linearGradient>
        </defs>
        <path
          d="M117.115 88.4834C79.7503 129.617 60.9267 182.884 35.4315 231.334C25.4765 250.256 13.7968 268.165 0.717773 285H389.369V0.839905C290.899 8.66522 189.863 8.40134 117.109 88.4834H117.115Z"
          style={{ mixBlendMode: 'multiply' }}
          fill={`url(#wave-g6)`}
        />
      </svg>
    </div>
  )
}
